import { useEffect, useMemo, useState } from 'react';

import { Button, Group, Paper, Text } from '@mantine/core';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { scudMappingApi } from '@/shared/api/scudMapping';
import { SCUD_COMMENT_MAX_LENGTH } from '@/shared/constant/scudMapping';
import { useShowNotifaction } from '@/shared/hooks/useShowNotifaction';
import {
	IScudFederationPerson,
	IScudLocationAccess,
	IScudMappingReason,
	IScudPersonExclusion,
} from '@/shared/types/scudMapping.types';
import { FieldCaption, LabelInput, Select } from '@/shared/uikit/components/input';
import { ModalSheet } from '@/shared/uikit/components/modalSheet';

import { IconUserSearch } from '@tabler/icons-react';

import { FederationSearchModal } from './FederationSearchModal';
import { SCUD_EXCLUSIONS_KEY } from './queryKeys';
import { employeeDetails, reasonLabel, requestErrorText } from './utils';

/** У исключения хранятся только id и ФИО сотрудника */
const exclusionEmployee = (exclusion: IScudPersonExclusion): IScudFederationPerson => ({
	person_id: exclusion.person_id_federation,
	full_name: exclusion.full_name_federation ?? `id ${exclusion.person_id_federation}`,
	is_main: true,
});

type ScudExclusionFormProps = {
	opened: boolean;
	/** null — новое исключение */
	exclusion: IScudPersonExclusion | null;
	onClose: () => void;
	/** Локации с правом обработки */
	locations: IScudLocationAccess[];
	reasons: IScudMappingReason[];
};

export const ScudExclusionForm = ({
	opened,
	exclusion,
	onClose,
	locations,
	reasons,
}: ScudExclusionFormProps) => {
	const queryClient = useQueryClient();
	const [employee, setEmployee] = useState<IScudFederationPerson | null>(null);
	const [location, setLocation] = useState<string | null>(null);
	const [reasonId, setReasonId] = useState<string | null>(null);
	const [comment, setComment] = useState('');
	const [isSearchOpened, setIsSearchOpened] = useState(false);

	useEffect(() => {
		if (!opened) {
			return;
		}

		setEmployee(exclusion ? exclusionEmployee(exclusion) : null);
		setLocation(exclusion?.location ?? (locations.length === 1 ? locations[0].location : null));
		setReasonId(exclusion?.reason_id ?? null);
		setComment(exclusion?.comment ?? '');
		setIsSearchOpened(false);
	}, [opened, exclusion?.id]);

	// Скрытую причину сервер не принимает (400): текущая остаётся видна, но выбрать её нельзя
	const reasonOptions = useMemo(
		() =>
			reasons
				.filter((item) => item.is_active || item.id === exclusion?.reason_id)
				.map((item) => ({ value: item.id, label: reasonLabel(item), disabled: !item.is_active })),
		[reasons, exclusion?.reason_id],
	);

	const reason = reasons.find((item) => item.id === reasonId) ?? null;
	const trimmedComment = comment.trim();

	const validationError = (() => {
		if (!employee) {
			return 'Выберите сотрудника';
		}

		if (!location) {
			return 'Выберите локацию';
		}

		if (!reason) {
			return 'Выберите причину';
		}

		if (!reason.is_active) {
			return `Причина «${reason.name}» больше не используется — выберите другую`;
		}

		if (reason.comment_required && !trimmedComment) {
			return `Для причины «${reason.name}» комментарий обязателен`;
		}

		return null;
	})();

	const handleSaved = (text: string) => {
		void queryClient.invalidateQueries({ queryKey: SCUD_EXCLUSIONS_KEY });
		useShowNotifaction({ text, type: 'success' });
		onClose();
	};

	const handleError = (err: unknown) =>
		useShowNotifaction({
			text: requestErrorText(err, 'Не удалось сохранить исключение'),
			type: 'error',
		});

	const { mutate: create, isPending: isCreating } = useMutation({
		mutationFn: scudMappingApi.createExclusion,
		onSuccess: () => handleSaved('Исключение добавлено'),
		onError: handleError,
	});

	const { mutate: update, isPending: isUpdating } = useMutation({
		mutationFn: scudMappingApi.updateExclusion,
		onSuccess: () => handleSaved('Исключение изменено'),
		onError: handleError,
	});

	const handleSubmit = () => {
		if (validationError || !employee || !location || !reasonId) {
			return;
		}

		const payload = {
			person_id_federation: employee.person_id,
			location,
			reason_id: reasonId,
			comment: trimmedComment || null,
		};

		if (exclusion) {
			update({ id: exclusion.id, payload });
		} else {
			create(payload);
		}
	};

	return (
		<>
			<ModalSheet
				title={exclusion ? 'Изменение исключения' : 'Новое исключение'}
				opened={opened}
				onClose={onClose}
				size="40rem"
			>
				<Text size="sm" c="dimmed">
					Сотрудник не попадает в отчёт «Бронировали, но нет пропуска» по выбранной локации
				</Text>
				<Paper withBorder radius="lg" p="sm">
					<Group justify="space-between" align="flex-start" wrap="nowrap">
						<div>
							<FieldCaption compact>Сотрудник в «Федерации»</FieldCaption>
							{employee ? (
								<>
									<Text size="sm" fw={700}>
										{employee.full_name}
									</Text>
									{employeeDetails(employee) && (
										<Text size="xs" c="dimmed">
											{employeeDetails(employee)}
										</Text>
									)}
									{exclusion && (
										<Text size="xs" c="dimmed">
											Сотрудника изменить нельзя: удалите исключение и добавьте новое
										</Text>
									)}
								</>
							) : (
								<Text size="sm" c="dimmed">
									Не выбран
								</Text>
							)}
						</div>
						{!exclusion && (
							<Button
								variant="light"
								leftSection={<IconUserSearch size={16} />}
								onClick={() => setIsSearchOpened(true)}
							>
								{employee ? 'Выбрать другого' : 'Найти сотрудника'}
							</Button>
						)}
					</Group>
				</Paper>
				<Select
					compact
					caption="Локация"
					placeholder="Выберите локацию"
					data={locations.map((item) => item.location)}
					value={location}
					onChange={setLocation}
					allowDeselect={false}
				/>
				<Select
					compact
					caption="Причина"
					placeholder="Выберите причину"
					data={reasonOptions}
					value={reasonId}
					onChange={setReasonId}
					allowDeselect={false}
				/>
				<LabelInput
					compact
					multiple
					label="Комментарий"
					minRows={2}
					maxRows={6}
					maxLength={SCUD_COMMENT_MAX_LENGTH}
					value={comment}
					onChange={(event) => setComment(event.currentTarget.value)}
					description={`${reason?.comment_required ? `Для причины «${reason.name}» комментарий обязателен. ` : ''}До ${SCUD_COMMENT_MAX_LENGTH} символов.`}
					inputWrapperOrder={['label', 'input', 'description', 'error']}
				/>
				<Group justify="flex-end" gap="sm">
					<Button variant="outline" onClick={onClose}>
						Отмена
					</Button>
					<Button
						disabled={!!validationError}
						loading={isCreating || isUpdating}
						onClick={handleSubmit}
						title={validationError ?? undefined}
					>
						Сохранить
					</Button>
				</Group>
			</ModalSheet>
			<FederationSearchModal
				opened={opened && !exclusion && isSearchOpened}
				onClose={() => setIsSearchOpened(false)}
				initialQuery={employee?.full_name}
				onSelect={(selected) => {
					setEmployee(selected);
					setIsSearchOpened(false);
				}}
			/>
		</>
	);
};
